import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import {
  Col,
  Row,
  Modal,
  Form,
  Spin,
  Button,
  Input,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import { postObtenerReporte } from 'api/reporteador';
import { getVehiculo } from 'api/recaudacion/vehiculos';
import { getUnidadDeRecaudacion, getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import Previewer from 'components/Previewer';

export const tiposDeTramitesReporteador = [
  { descripcion: 'Alta' },
  { descripcion: 'Compraventa' },
  { descripcion: 'Cambio de Propietario' },
  { descripcion: 'Refrendo' },
  { descripcion: 'Cambio de Motor' },
];

export const imprimirTarjetaDeCirculacionVehiculo = async (
  setLoading = () => { },
  tramite,
  selectedRowKey,
  unidad_de_recaudacion,
  anio,
  callback = () => { },
) => {
  setLoading(true);
  const match = await getVehiculo(selectedRowKey);
  const _unidadDeRecaudacion = await getUnidadDeRecaudacion(unidad_de_recaudacion || 1);
  const fechaDeExpedicion = new Moment().format(DATE_FRONTEND_FORMAT.replace('\\/', '-'));
  const values = {
    ID: match.es_propietario_ciudadano ? 43 : 45,
    Parametros: [
      {
        Nombre: 'clave',
        TipoParametro: 'Texto',
        Valor: match.clave,
      },
      {
        Nombre: 'fecha_expedicion',
        TipoParametro: 'Texto',
        Valor: fechaDeExpedicion,
      },
      {
        Nombre: 'clave_recaudadora',
        TipoParametro: 'Texto',
        Valor: _unidadDeRecaudacion.clave,
      },
      {
        Nombre: 'marca',
        TipoParametro: 'Texto',
        Valor: tramite.marca,
      },
      {
        Nombre: 'tramite',
        TipoParametro: 'Texto',
        Valor: tramite.tramite,
      },
      {
        Nombre: 'anio',
        TipoParametro: 'Texto',
        Valor: tramite.anio,
      },
    ],
  };
  const file = await postObtenerReporte(values);
  callback(file);
  setLoading(false);
  return file;
};

const ModalTramiteTarjeta = ({
  visible,
  setVisible,
  selectedRowKey,
  tipoDeTramite,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [unidadesDeRecaudacion, setUnidadesDeRecaudacion] = useState([]);
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [base64, setBase64] = useState();

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _unidadesRecaudadoras = await getUnidadesDeRecaudacion();
      if (mounted) {
        setUnidadesDeRecaudacion(_unidadesRecaudadoras);
        setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onFinish = async () => {
    try {
      const values = form.getFieldsValue();
      setLoading(true);
      await imprimirTarjetaDeCirculacionVehiculo(
        setLoading,
        values,
        selectedRowKey,
        values.unidad_de_recaudacion,
        (file) => {
          setBase64(file);
          setVisiblePreview(true);
        },
      );
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const saveControl = {
    text: 'Guardar',
    icon: <SaveOutlined />,
    onClick: () => onFinish(),
  };

  const backControl = {
    text: 'Regresar',
    icon: <ArrowLeftOutlined />,
    onClick: () => onCancel(),
  };

  const getControls = () => [backControl, saveControl];

  const rules = { required: [{ required: true }] };

  return (
    <Modal
      footer={null}
      closable={false}
      visible={visible}
      loading={loading}
      title={(
        <FormSubmitControls
          loading={loading}
          label="Datos Necesarios para la Tarjeta"
          controls={getControls()}
        />
      )}
    >
      <Spin tip="Cargando..." spinning={loading}>
        <Row>
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              name="tarjeta"
              onFinish={() => onFinish()}
              initialValues={{ tramite: tipoDeTramite }}
            >
              <Col span={24}>
                <Form.Item
                  name="marca"
                  label="Marca y Submarca"
                  hasFeedback
                  rules={rules.required}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="tramite"
                  label="Tipo de Tramite"
                  hasFeedback
                  rules={rules.required}
                >
                  <Select
                    dataSource={tiposDeTramitesReporteador}
                    valueProp="descripcion"
                    disabled={!!tipoDeTramite}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Unidad de Recaudación"
                  rules={rules.required}
                  name="unidad_de_recaudacion"
                >
                  <Select
                    dataSource={unidadesDeRecaudacion}
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Spin>
      <Previewer
        visible={visiblePreview}
        setVisible={setVisiblePreview}
        base64={base64}
      />
    </Modal>
  );
};

ModalTramiteTarjeta.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  selectedRowKey: PropTypes.number,
  tipoDeTramite: PropTypes.string,
};

ModalTramiteTarjeta.defaultProps = {
  selectedRowKey: null,
  tipoDeTramite: null,
};
export default ModalTramiteTarjeta;
